import { useState } from "react";
import { getHeaders } from "../../utils/authHelpers";
import { getApiUrl } from "../../utils/api";
import devLogger from "../../utils/devLogger";

export const useForgotPassword = (): UseForgotPasswordReturn => {
  const [currentStep, setCurrentStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const resetError = () => setError("");

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError("");
    setIsLoading(true);

    try {
      // Send request to get a password reset token
      const response = await fetch(
        getApiUrl("/auth/password/forgot-password"),
        {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        devLogger.error("🚀 ~ errorData:", errorData);
        throw new Error(errorData.message || "Failed to send reset token");
      }

      setCurrentStep("token");
    } catch (err: any) {
      setError(err.message || "Failed to send reset token. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (token.length !== 6 || !/^\d+$/.test(token)) {
      setError("Please enter a valid 6-digit token");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      // Verify the token is valid
      const response = await fetch(getApiUrl("/auth/password/verify-token"), {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ email, token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        devLogger.error("🚀 ~ errorData:", errorData);
        throw new Error(errorData.message || "Invalid token");
      }

      setCurrentStep("password");
    } catch (err: any) {
      setError(err.message || "Invalid token. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      // Reset the password using the token
      const response = await fetch(getApiUrl("/auth/password/reset-password"), {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          email,
          newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        devLogger.error("🚀 ~ errorData:", errorData);
        throw new Error(errorData.message || "Failed to update password");
      }

      setCurrentStep("success");
    } catch (err: any) {
      setError(err.message || "Failed to update password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    currentStep,
    email,
    token,
    newPassword,
    confirmPassword,
    error,
    isLoading,
    setCurrentStep,
    setEmail,
    setToken,
    setNewPassword,
    setConfirmPassword,
    handleEmailSubmit,
    handleTokenSubmit,
    handlePasswordSubmit,
    resetError,
  };
};

type Step = "email" | "token" | "password" | "success";

interface UseForgotPasswordReturn {
  currentStep: Step;
  email: string;
  token: string;
  newPassword: string;
  confirmPassword: string;
  error: string;
  isLoading: boolean;
  setCurrentStep: (step: Step) => void;
  setEmail: (email: string) => void;
  setToken: (token: string) => void;
  setNewPassword: (password: string) => void;
  setConfirmPassword: (password: string) => void;
  handleEmailSubmit: (e: React.FormEvent) => Promise<void>;
  handleTokenSubmit: (e: React.FormEvent) => Promise<void>;
  handlePasswordSubmit: (e: React.FormEvent) => Promise<void>;
  resetError: () => void;
}
